import {
  Subscription,
  Plan,
  SubscriptionFeature,
  SubscriptionUpdatePreview,
} from "@/types/auth";
import { api } from "../api";

const VALID_SUBSCRIPTION_FEATURES: SubscriptionFeature[] = [
  "showcase",
  "site-analytics",
];

type UpdateCurrentSubscriptionResponse = {
  checkoutLink?: string;
  // Compatibilidade com respostas antigas
  checkoutUrl?: string;
};

let currentSubscriptionCache: Subscription | null = null;
let pendingCurrentSubscriptionRequest: Promise<Subscription> | null = null;

export const invalidateCurrentSubscriptionCache = (): void => {
  currentSubscriptionCache = null;
  pendingCurrentSubscriptionRequest = null;
};

interface GetUpdatePreviewParams {
  newPlanId: string;
  promotionCode?: string;
}

interface GetUpdatePreviewOptions {
  suppressErrorToast?: boolean;
}

export const subscriptionsService = {
  // Buscar assinatura atual
  async getCurrentSubscription(forceRefresh = false): Promise<Subscription> {
    if (pendingCurrentSubscriptionRequest) {
      return pendingCurrentSubscriptionRequest;
    }

    if (!forceRefresh && currentSubscriptionCache) {
      return currentSubscriptionCache;
    }

    pendingCurrentSubscriptionRequest = (async () => {
      try {
        const response = await api.get("/billing/subscription");
        const data = response.data as Subscription;

        currentSubscriptionCache = {
          ...data,
          // Compatibilidade com telas legadas que usam maxUsers
          maxUsers: data.maxUsers ?? data.users ?? 0,
          features: (data.features ?? [])
            .map((feature) => feature.toLowerCase().trim())
            .filter((feature): feature is SubscriptionFeature =>
              VALID_SUBSCRIPTION_FEATURES.includes(
                feature as SubscriptionFeature,
              ),
            ),
        };

        return currentSubscriptionCache;
      } catch (error: any) {
        // Se o endpoint indicar ausência de assinatura com mensagem específica,
        // disparar o estado global de assinatura (mesma ação do interceptor)
        try {
          const msg = error.response?.data?.message || "";
          const lower = (msg || "").toLowerCase();

          // Tratar caso de conta desativada (NO_ACTIVE_ACCOUNT)
          const backendCode = error.response?.data?.code as string | undefined;
          if (
            error.response?.status === 403 &&
            backendCode === "NO_ACTIVE_ACCOUNT"
          ) {
            const apiModule = await import("../api");
            if (
              apiModule &&
              typeof apiModule.triggerSubscriptionError === "function"
            ) {
              apiModule.triggerSubscriptionError(
                error.response?.data?.message || msg,
                "/dash/contratar-conta/",
              );
            }
            throw new Error("Conta desativada. Acesse contratar conta.");
          }

          const isSubscriptionMissing =
            error.response?.status === 404 &&
            typeof msg === "string" &&
            (lower.includes("account does not have a current subscription") ||
              lower.includes("account has no current subscription") ||
              lower.includes("account does not have a subscription") ||
              lower.includes("account has no subscription") ||
              lower.includes("no current subscription"));

          if (isSubscriptionMissing) {
            // Import dinamicamente para evitar ciclos
            const apiModule = await import("../api");
            if (
              apiModule &&
              typeof apiModule.triggerSubscriptionError === "function"
            ) {
              apiModule.triggerSubscriptionError(msg);
            }
            throw new Error("Assinatura não encontrada.");
          }
        } catch (e) {
          // ignore errors here and continue to map status codes below
        }

        if (error.response?.status === 401) {
          throw new Error("Usuário não autenticado. Faça login novamente.");
        } else if (error.response?.status === 404) {
          throw new Error("Assinatura não encontrada.");
        }
        throw new Error(
          error.response?.data?.message || "Erro ao buscar assinatura atual",
        );
      } finally {
        pendingCurrentSubscriptionRequest = null;
      }
    })();

    return pendingCurrentSubscriptionRequest;
  },

  // Cancelar assinatura atual
  async cancelCurrentSubscription(): Promise<void> {
    try {
      await api.delete("/billing/subscriptions/current");
      invalidateCurrentSubscriptionCache();
    } catch (error: any) {
      if (error.response?.status === 401) {
        throw new Error("Usuário não autenticado. Faça login novamente.");
      } else if (error.response?.status === 404) {
        throw new Error("Assinatura não encontrada.");
      }
      throw new Error(
        error.response?.data?.message || "Erro ao cancelar assinatura",
      );
    }
  },

  // Buscar sessão do portal de gerenciamento
  async getPortalSession(): Promise<{ url: string }> {
    try {
      const response = await api.get("/billing/subscription/portal-session");
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 401) {
        throw new Error("Usuário não autenticado. Faça login novamente.");
      }
      throw new Error(
        error.response?.data?.message ||
          "Erro ao acessar portal de gerenciamento",
      );
    }
  },

  // Atualizar assinatura atual
  async updateCurrentSubscription(
    planId: string,
    promotionCode?: string,
  ): Promise<UpdateCurrentSubscriptionResponse> {
    try {
      const response = await api.put("/billing/subscription", {
        planId,
        ...(promotionCode ? { promotionCode } : {}),
      });
      invalidateCurrentSubscriptionCache();
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 401) {
        throw new Error("Usuário não autenticado. Faça login novamente.");
      } else if (error.response?.status === 404) {
        throw new Error("Assinatura não encontrada.");
      }
      // Re-throw original error to preserve _errorMessageShown set by the interceptor
      throw error;
    }
  },

  async getUpdatePreview(
    params: GetUpdatePreviewParams,
    options?: GetUpdatePreviewOptions,
  ): Promise<SubscriptionUpdatePreview> {
    try {
      const response = await api.get<SubscriptionUpdatePreview>(
        "/billing/subscription/update-preview",
        {
          params: {
            newPlanId: params.newPlanId,
            ...(params.promotionCode
              ? { promotionCode: params.promotionCode }
              : {}),
          },
          ...(options?.suppressErrorToast
            ? { _suppressErrorMessage: true }
            : {}),
        },
      );
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 401) {
        throw new Error("Usuário não autenticado. Faça login novamente.");
      } else if (error.response?.status === 404) {
        throw new Error("Prévia da assinatura não encontrada.");
      }
      throw error;
    }
  },

  // Buscar planos disponíveis
  async getAvailablePlans(): Promise<Plan[]> {
    try {
      const response = await api.get<Plan[]>("/billing/plans");
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 401) {
        throw new Error("Usuário não autenticado. Faça login novamente.");
      }
      throw new Error(
        error.response?.data?.message || "Erro ao buscar planos disponíveis",
      );
    }
  },
};
